import { IEnvelope, IDocument, IBlock, IExecutedDocument } from '../types';
import { ActionTypes } from '../actions';
import update from 'immutability-helper';

export interface IDownloadedFile {
  alreadySigned: boolean;
}
export interface IEnvelopeState {
  envelope?: IEnvelope;
  isSubmitting: boolean;
  requestPending: boolean;
  signingActionsMenuOpen: boolean;
  serverTime: string;
  downloadedFile: IDownloadedFile;
  metadata: {
    ownerRegionCountries: string;
    ownerSubscriberId: string;
  };
  pageDimensions: {
    [fullPage: number]: {
      height?: number;
      width?: number;
    };
  };
  pageScale: {
    [fullPage: number]: number;
  };
  currentDocumentId: string;
  currentDocumentName: string;
}

export const initialEnvelopeState: IEnvelopeState = {
  envelope: undefined,
  isSubmitting: false,
  requestPending: false,
  signingActionsMenuOpen: false,
  serverTime: '',
  downloadedFile: {
    alreadySigned: false,
  },
  metadata: {
    ownerRegionCountries: '',
    ownerSubscriberId: ''
  },
  pageDimensions: {},
  pageScale: {},
  currentDocumentId: '',
  currentDocumentName: '',
};

const envelopeReducer = (state: IEnvelopeState = initialEnvelopeState, action: any): IEnvelopeState => {
  switch (action.type) {
    case ActionTypes.FETCH_ENVELOPE_DONE: {
      const envelope = action.payload;
      const { serverTime } = action.payload;
      envelope.documents.forEach((doc: IDocument, index: number) => {
        if (!doc.order) {
          doc.order = index + 1;
        }
      });
      return {
        ...state,
        serverTime,
        envelope,
      };
    }
    case ActionTypes.SUBMIT_ENVELOPE: {
      return {
        ...state,
        isSubmitting: true,
      };
    }
    case ActionTypes.SUBMIT_ENVELOPE_DONE: {
      const { executedDocuments, signer } = action.payload || state.envelope!;
      if (executedDocuments) {
        executedDocuments.forEach((_: IExecutedDocument, index: number) => {
          const pdf = state.envelope?.documents.find(({ order }) => order === index + 1);
          const executedDocument = executedDocuments.find(({ documentId }: any) => documentId === pdf?.documentId);
          executedDocument.order = pdf?.order!;
        });
        executedDocuments.sort((a: IExecutedDocument, b: IExecutedDocument) => {
          return a.order - b.order;
        });
      }
      return update(state, {
        envelope: {
          status: { $set: 'Completed' },
          signer: {
            executedDocuments: { $set: executedDocuments },
            dateSigned: { $set: signer.dateSigned },
            signingStatus: { $set: 'Complete'}
          },
        },
        isSubmitting: { $set: false },
      });
    }
    case ActionTypes.ERROR: {
      return {
        ...state,
        isSubmitting: false,
      };
    }
    case ActionTypes.START_REQUEST: {
      return update(state, {
        requestPending: { $set: true },
      });
    }
    case ActionTypes.END_REQUEST: {
      return update(state, {
        requestPending: { $set: false },
      });
    }
    case ActionTypes.FETCH_THUMBNAILS_BY_ENVELOPE_DONE: {
      let { pageThumbnails } = action.payload;
      const documents = state.envelope!.documents;

      const updates = {};
      pageThumbnails.forEach((doc: any) => {
        const docIndex = documents.findIndex(d => d.documentId === doc.documentId);
        updates[docIndex] = { pageThumbnails: { $set: doc.thumbnails } };
      });
      return update(state, {
        envelope: { documents: updates },
      });
    }
    case ActionTypes.DOWNLOAD_ENVELOPE_DONE: {
      const { blobUrl, fileName, alreadySigned, fileType } = action.payload;
      const downloadedFile = { ...state.downloadedFile, alreadySigned };

      downloadedFile[fileType] = {
        blobUrl,
        fileName,
      };

      return {
        ...state,
        downloadedFile,
      };
    }
    case ActionTypes.SET_PAGE_DIMENSIONS: {
      const { height, width, fullPage } = action.payload;
      return update(state, {
        pageDimensions: {
          [fullPage]: {
            $set: {
              height: height,
              width: width,
            },
          },
        },
      });
    }
    case ActionTypes.SET_PAGE_SCALE: {
      const { fullPage, pageScale } = action.payload;
      return update(state, {
        pageScale: {
          [fullPage]: { $set: pageScale },
        },
      });
    }
    case ActionTypes.SET_CURRENT_DOCUMENT: {
      const { documentId, documentName } = action.payload;
      return update(state, {
        currentDocumentId: { $set: documentId },
        currentDocumentName: { $set: documentName },
      });
    }
    case ActionTypes.UPDATE_SIGNER_SIGNING_SETTINGS: {
      return update(state, {
        envelope: {
          signer: {
            signingSettings: { $set: action.payload }
          }
        }
      })
    }
    default: {
      return state;
    }
  }
};

export default envelopeReducer;
